/**
 * Household renewal rules (#127 slice 6, requirements R7, design.md §8).
 *
 * Pure functions shared by process-payment (online renewal),
 * membership-renewal-lambda (admin renewal and the January cron),
 * get-own-member-profile (the amount the dashboard shows) and the admin UI.
 *
 * A family renews as one: only the primary renews, the price is the household
 * price (D1/D2) for the renewal year, and every renewable row in the family
 * (status VERIFIED, any renewalStatus) is renewed together. isActive is NOT
 * used to pick rows: the grace-period cron sets isActive=false on every
 * unrenewed row, and those rows must still be counted and renewed.
 */
import { clubYear, familyRoleForYear, isJuniorForYear } from './membership';
import { familyPriceCents, getFeeCents, membershipTypeToFeeKey } from './fees';
import { householdFromMembers } from './familyLimits';

export interface RenewalMemberRow {
  id: string;
  status?: string | null;
  membershipType?: string | null;
  familyGroupId?: string | null;
  familyPrimary?: boolean | null;
  familyRole?: string | null;
  dateOfBirth?: string | null;
  isJunior?: boolean | null;
  email?: string | null;
}

/** True for a FAMILY member who is not the primary: they are renewed by the primary and can't renew themselves. */
export function isFamilyDependant(member: Pick<RenewalMemberRow, 'membershipType' | 'familyGroupId' | 'familyPrimary'> | null | undefined): boolean {
  return !!member && member.membershipType === 'FAMILY' && !!member.familyGroupId && member.familyPrimary !== true;
}

/** The rows a household renewal renews: every VERIFIED row in the family. */
export function renewableFamilyRows<T extends RenewalMemberRow>(familyRows: T[]): T[] {
  return familyRows.filter((r) => r.status === 'VERIFIED');
}

/**
 * Expected renewal amount in cents for the year being renewed (`clubYear(now)`).
 * FAMILY (primary, with its family rows): the household price for the renewable
 * rows. Everyone else: the flat fee for their type. undefined if the type is unknown.
 */
export function expectedRenewalCents(
  member: RenewalMemberRow,
  familyRows: RenewalMemberRow[] | null | undefined,
  now: Date = new Date(),
): number | undefined {
  const feeKey = member.membershipType ? membershipTypeToFeeKey(member.membershipType) : undefined;
  if (!feeKey) return undefined;
  if (member.membershipType !== 'FAMILY' || !member.familyGroupId) return getFeeCents(feeKey);
  const rows = renewableFamilyRows(familyRows && familyRows.length > 0 ? familyRows : [member]);
  // isActive is deliberately dropped: an expired family is still priced in full.
  const household = householdFromMembers(rows.map((r) => ({ dateOfBirth: r.dateOfBirth })), clubYear(now));
  return familyPriceCents(household);
}

/** Placeholder addresses created for members without an email; never send mail to them. */
export function isPlaceholderEmail(email: string | null | undefined): boolean {
  const e = (email || '').trim().toLowerCase();
  return !e || e.startsWith('noemail-') || e.endsWith('@noemail.tumbyfc.com');
}

/**
 * Stored flags to refresh for `year` (Q3, 1 January): isJunior from the age
 * rule, and familyRole for non-primary family members. Returns only the fields
 * that change; {} when nothing changes or the DOB is missing.
 */
export function ageFlagRefresh(member: RenewalMemberRow, year: number): { isJunior?: boolean; familyRole?: 'ADULT' | 'CHILD' } {
  if (!member.dateOfBirth) return {};
  const out: { isJunior?: boolean; familyRole?: 'ADULT' | 'CHILD' } = {};
  const junior = isJuniorForYear(member.dateOfBirth, year);
  if (member.isJunior !== junior) out.isJunior = junior;
  if (member.familyGroupId && member.familyPrimary !== true && member.familyRole !== 'PRIMARY') {
    const role = familyRoleForYear(member.dateOfBirth, year);
    if (member.familyRole !== role) out.familyRole = role;
  }
  return out;
}
